import { getPortalApiBase } from '@/lib/env'

/**
 * La última versión de TECOPOS escritorio (la caja de Windows), según el
 * `latest.yml` del canal de actualizaciones.
 *
 * El navegador NO puede leer ese fichero: `repo.tecopos.com` no manda
 * `Access-Control-Allow-Origin`. Lo lee `tecopos-portal-server` en
 * `GET /downloads/pv-desktop/latest`, con caché y con el último valor bueno si
 * el repo se cae; aquí solo se pide y se comprueba la forma.
 */
export interface PvDesktopLatest {
  version: string
  url: string
  releaseDate: string | null
}

/** El id de la tarjeta del CMS que se alimenta de este endpoint. */
export const PV_DESKTOP_APP_ID = 'd-pv-windows'

/** La página no espera más que esto: sin respuesta, se queda con el CMS. */
export const PV_DESKTOP_TIMEOUT_MS = 6_000

const VERSION_RE = /^[0-9]+(\.[0-9]+){0,3}([-+][0-9A-Za-z.-]{1,32})?$/

/**
 * Pide la última versión. Devuelve `null` ante CUALQUIER fallo —red, 503, un
 * cuerpo raro, el timeout—: la tarjeta cae entonces al `windowsUrl` del CMS y
 * la página no se entera. Conectividad cubana: nunca se lanza.
 */
export async function fetchPvDesktopLatest(signal?: AbortSignal): Promise<PvDesktopLatest | null> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), PV_DESKTOP_TIMEOUT_MS)
  const onAbort = () => controller.abort()
  signal?.addEventListener('abort', onAbort)
  try {
    const res = await fetch(`${getPortalApiBase()}/downloads/pv-desktop/latest`, {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
    if (!res.ok) return null
    const data = (await res.json()) as Record<string, unknown> | null
    if (!data || typeof data !== 'object') return null
    const { version, url, releaseDate } = data
    if (typeof version !== 'string' || !VERSION_RE.test(version)) return null
    if (typeof url !== 'string' || !url) return null
    return {
      version,
      url,
      releaseDate: typeof releaseDate === 'string' ? releaseDate : null,
    }
  } catch {
    return null
  } finally {
    clearTimeout(timer)
    signal?.removeEventListener('abort', onAbort)
  }
}
